<template>
  <div>
    <v-dialog
      v-model="show"
      max-width="60vh"
      scrollable
    >
      <v-card>
        <v-card-title>
          Military Categories
          <v-spacer></v-spacer>
          <v-btn
            icon
            @click="show = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="py-3">
          <v-data-table
            dense
            fixed-header
            height="60vh"
            :headers="tableHeaders"
            :items="militaryCategories"
            :items-per-page="-1"
            hide-default-footer
          >
            <template v-slot:[`item.Category_Name`]="{ item, index }">
              <v-text-field
                :rules="[rules.required, rules.alreadyExist]"
                v-if="editMode && currentRowIndex === index"
                v-model="currentItem.Category_Name"
              ></v-text-field>
              <span v-else>{{ item.Category_Name }}</span>
            </template>
            <template v-slot:[`item.Actions`]="{ item , index}">
              <v-btn
                :disabled="editMode && (currentRowIndex !== index) || (currentRowIndex === index && canUpdate)"
                :color="editMode && currentRowIndex === index ? 'success' : 'primary'"
                small
                icon
                @click="onEditClick(item, index)"
              >
                <v-icon>
                  {{ currentRowIndex === index ? 'mdi-check-circle' : 'mdi-pencil-circle' }}
                </v-icon>
              </v-btn>
              <v-btn
                small
                icon
                color="red darken-2"
                v-if="index !== currentRowIndex"
                :disabled="editMode"
                @click="onDeleteClick(item)"
              >
                <v-icon>mdi-delete-circle</v-icon>
              </v-btn>
              <v-btn
                v-if="editMode && currentRowIndex === index"
                color="primary"
                small
                icon
                @click="onCancelClick"
              >
                <v-icon>mdi-close-circle</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="success"
            small
            @click="createNewCategoryDialog = !createNewCategoryDialog"
          >
            Create New
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      max-width="500"
      v-model="createNewCategoryDialog"
    >
      <v-card>
        <v-card-title>
          New Military Category
          <v-spacer></v-spacer>
          <v-btn
            icon
            @click="createNewCategoryDialog = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-4">
          <v-text-field
            v-model="newCategoryName"
            label="Category Name"
            autofocus
            :rules="[rules.required, rules.alreadyExist]"
          ></v-text-field>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            small
            color="primary"
            :disabled="canSave"
            @click="createNewMilitaryCategory"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <ConfirmOrDeniedDialog
      v-if="deleteCategoryDialog"
      v-model="deleteCategoryDialog"
      :mode="confirmOrDeniedDialogModes.delete"
      @onDeleteClick="deleteMilitaryCategory"
      @onCancelClick="cancelDelete"
    />
  </div>
</template>
<script>
export default {
  name: 'MilitaryCategoriesManager',
  components: {
    ConfirmOrDeniedDialog: () => import('@/components/global/ConfirmOrDeniedDialog.vue')
  },
  props: {
    value: {
      required: false,
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      currentRowIndex: null,
      currentItem: null,
      createNewCategoryDialog: false,
      militaryCategories: [],
      deleteCategoryDialog: false,
      editMode: false,
      newCategoryName: '',
      tableHeaders: [
        {
          text: 'Category Name',
          value: 'Category_Name',
          divider: true,
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap'
        },
        {
          text: '#',
          value: 'Actions',
          align: 'center',
          cellClass: 'px-2 text-no-wrap',
          class: 'px-2 text-no-wrap',
          width: 100,
          sortable: false
        }
      ],
      rules: {
        required: v => !!v || 'Required',
        alreadyExist: (val) => this.validateOnAlreadyExist(val) || 'The category name already exists'
      }
    }
  },
  mounted () {
    this.getMilitaryCategories()
  },
  computed: {
    user () {
      return this.$store.getters.getUser
    },
    show: {
      get () {
        return this.value
      },
      set (value) {
        this.$emit('input', value)
      }
    },
    canUpdate () {
      return !this.currentItem || !this.currentItem.Category_Name || !this.validateOnAlreadyExist(this.currentItem.Category_Name)
    },
    canSave () {
      return !this.newCategoryName || (this.newCategoryName && !this.validateOnAlreadyExist(this.newCategoryName))
    },
    confirmOrDeniedDialogModes () {
      return this.$store.getters.getConfirmOrDeniedDialogModes
    }
  },
  methods: {
    validateOnAlreadyExist (val) {
      return !this.militaryCategories.some(el => el.Category_Name === val.trim())
    },
    onEditClick (item, index) {
      if (this.editMode) {
        this.updateMilitaryCategory()
      } else {
        this.tableHeaders[0].sortable = false
        this.currentItem = Object.assign({}, item)
        this.currentRowIndex = index
      }
      this.editMode = !this.editMode
    },
    onCancelClick () {
      this.editMode = false
      this.currentItem = null
      this.currentRowIndex = null
      this.tableHeaders[0].sortable = true
    },
    onDeleteClick (item) {
      this.deleteCategoryDialog = true
      this.currentItem = Object.assign({}, item)
    },
    cancelDelete () {
      this.deleteCategoryDialog = false
      this.currentItem = null
    },
    getMilitaryCategories () {
      this.axios.get('/api/military_categories', {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.militaryCategories = res.data
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    },
    createNewMilitaryCategory () {
      this.axios.post('/api/military_category', {
        Category_Name: this.newCategoryName.trim()
      }, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.createNewCategoryDialog = false
        this.newCategoryName = ''
        this.getMilitaryCategories()
        this.$store.dispatch('setSuccess', { successMsg: 'New military category successfully created' })
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    },
    updateMilitaryCategory () {
      this.axios.put(`/api/military_category/${this.currentItem.Military_Category_ID}`, {
        Category_Name: this.currentItem.Category_Name.trim()
      }, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.getMilitaryCategories()
        this.onCancelClick()
        this.$store.dispatch('setSuccess', { successMsg: 'Military category successfully updated' })
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    },
    deleteMilitaryCategory () {
      this.axios.delete(`/api/military_category/${this.currentItem.Military_Category_ID}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.getMilitaryCategories()
        this.cancelDelete()
        this.$store.dispatch('setSuccess', { successMsg: 'Military category successfully deleted' })
      }).catch(error => {
        const errorMsg = error.response?.data || error.message

        if (errorMsg.includes('conflicted with the REFERENCE constraint')) {
          this.$store.dispatch('setError', { errorMsg: 'Cannot delete category because it is used in another module.' })
        } else {
          this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + errorMsg })
        }
      })
    }
  }
}
</script>
